import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './NewRequest.css';
import authService from '../services/authService';

const NewRequest = ({ phoneNumber, token, onBack }) => {
  const [formData, setFormData] = useState({
    title: '',
    case_type: '',
    description: '',
    case_location: '',
    lawyer_id: '',
  });

  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lawyersLoading, setLawyersLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch lawyers on component mount
  useEffect(() => {
    if (token) {
      fetchLawyers();
    } else {
      setError('Authentication token not found. Please log in again.');
    }
  }, [token]);

  const fetchLawyers = async () => {
    try {
      setLawyersLoading(true);
      console.log('🔍 Starting to fetch lawyers with token...');
      const response = await authService.getLawyers(token);
      console.log('🔍 Lawyers API response:', response);
      
      // Handle different possible response formats
      let lawyersList = [];
      if (response && response.data) {
        console.log('✅ Found lawyers in response.data');
        lawyersList = response.data;
      } else if (response && Array.isArray(response)) {
        console.log('✅ Response is an array');
        lawyersList = response;
      } else if (response && response.lawyers) {
        console.log('✅ Found lawyers in response.lawyers');
        lawyersList = response.lawyers;
      } else if (response && response.success && Array.isArray(response.success)) {
        console.log('✅ Found lawyers in response.success');
        lawyersList = response.success;
      } else if (response && typeof response === 'object') {
        console.log('✅ Checking for array in response properties');
        const firstArrayProp = Object.values(response).find(v => Array.isArray(v));
        if (firstArrayProp) {
          lawyersList = firstArrayProp;
          console.log('✅ Found array in response properties');
        }
      }
      
      if (lawyersList.length === 0) {
        console.warn('⚠️ No lawyers found in response');
      }
      
      setLawyers(lawyersList);
      setError('');
    } catch (err) {
      console.error('❌ Error fetching lawyers:', err);
      console.error('❌ Error message:', err.message);
      setError(`Failed to load lawyers: ${err.message}`);
      setLawyers([]);
    } finally {
      setLawyersLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.case_type.trim()) {
      setError('Case type is required');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return false;
    }
    if (!formData.case_location.trim()) {
      setError('Case location is required');
      return false;
    }
    if (!formData.lawyer_id) {
      setError('Please select a lawyer');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      const response = await authService.createCase({
        title: formData.title,
        case_type: formData.case_type,
        description: formData.description,
        case_location: formData.case_location,
        lawyer_id: formData.lawyer_id,
      }, token);

      // Show success alert
      await Swal.fire({
        title: 'Success!',
        text: 'Case created successfully!',
        icon: 'success',
        confirmButtonColor: '#3085d6'
      });
      
      // Reset form
      setFormData({
        title: '',
        case_type: '',
        description: '',
        case_location: '',
        lawyer_id: '',
      });

      // Go back after success
      onBack();
    } catch (err) {
      console.error('Error creating case:', err);
      // Show error alert
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to create case. Please try again.',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-request-container">
      <div className="new-request-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h2>New Request</h2>
      </div>

      <div className="new-request-form">
        {error && <div className="alert alert-error">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <form onSubmit={handleSubmit}>
          {/* Title Field */}
          <div className="form-group">
            <label htmlFor="title" className="form-label">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-input"
              placeholder="Enter case title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>

          {/* Case Type Field */}
          <div className="form-group">
            <label htmlFor="case_type" className="form-label">Case Type</label>
            <input
              type="text"
              id="case_type"
              name="case_type"
              className="form-input"
              placeholder="Enter case type"
              value={formData.case_type}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>

          {/* Description Field */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea
              id="description"
              name="description"
              className="form-input form-textarea"
              placeholder="Enter case description"
              rows="5"
              value={formData.description}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>

          {/* Case Location Field */}
          <div className="form-group">
            <label htmlFor="case_location" className="form-label">Case Location</label>
            <input
              type="text"
              id="case_location"
              name="case_location"
              className="form-input"
              placeholder="Enter case location"
              value={formData.case_location}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>

          {/* Lawyer Selection Dropdown */}
          <div className="form-group">
            <label htmlFor="lawyer_id" className="form-label">Select Lawyer</label>
            {lawyersLoading ? (
              <div className="loading-spinner">Loading lawyers...</div>
            ) : lawyers.length === 0 ? (
              <div className="no-lawyers-message">
                No lawyers available. Please try again later.
              </div>
            ) : (
              <select
                id="lawyer_id"
                name="lawyer_id"
                className="form-input form-select"
                value={formData.lawyer_id}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">-- Select a Lawyer --</option>
                {lawyers.map(lawyer => (
                  <option key={lawyer.id} value={lawyer.id}>
                    {lawyer.first_name} {lawyer.last_name} 
                    {lawyer.consultation_fee ? ` (${lawyer.consultation_fee})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={loading || lawyersLoading}
          >
            {loading ? 'Creating Request...' : 'Create Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewRequest;
